import { useState, useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import craiteriaLogoImage from '../../public/craiteria.png';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { SelectedFilter } from "../components/Filters/types";
import { FilterTableKey } from "../redux/filtersSlice";
import { getGeneralParams } from "../features/admin/generalParams/api/generalParamsAPI";
import { useUser } from "../utils/auth";
import { CsvAuditPayload } from "../types/DownloadCsvTypes";
import { addAuditLog } from "../features/auditLogs/api/auditApi";
import { KeyValuePair } from "../types/KeyValuePair";
import { FilterDto, FilterType } from "../types/FilterDto";
import { convertIntUnixToFormateDate } from "../utils/dateUtils";

// ─── Constants ────────────────────────────────────────────────────────────────
const CANVAS_SCALE   = 3;   // 3× supersampling → crisp text & lines
const PDF_MARGIN     = 12;  // mm
const LOGO_W         = 32;  // mm
const LOGO_H         = 25;  // mm
const FOOTER_H       = 14;  // mm  reserved at bottom of every page
const SECTION_GAP    = 8;   // mm  vertical gap between sections
const CAPTURE_DELAY  = 300; // ms  wait for charts/SVGs to finish painting

const HTML2CANVAS_OPTS = {
    useCORS: true,
    allowTaint: true,
    scale: CANVAS_SCALE,
    logging: false,
    scrollX: 0,
    scrollY: 0,
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────
interface ImgRecord {
    data: string;
    naturalW: number; // canvas px (already × CANVAS_SCALE)
    naturalH: number;
    cssW: number;     // original element CSS px width  (pre-scale)
    cssH: number;     // original element CSS px height (pre-scale)
}

// ─── Locale helpers ───────────────────────────────────────────────────────────
const LOCALE_MAP: Record<string, string> = {
    es: "es-ES",
    ca: "ca-ES",
    fr: "fr-FR",
    en: "en-US",
};

const TRANSLATIONS = {
    en: { filters: "Filters",  reportDate: "Date",  reportTime: "Time",  page: "Page",   of: "of" },
    es: { filters: "Filtros",  reportDate: "Fecha", reportTime: "Hora",  page: "Página", of: "de" },
    ca: { filters: "Filtres",  reportDate: "Data",  reportTime: "Hora",  page: "Pàgina", of: "de" },
    fr: { filters: "Filtres",  reportDate: "Date",  reportTime: "Heure", page: "Page",   of: "de" },
};

function getLocale(lang: string) {
    return LOCALE_MAP[lang] ?? "en-US";
}

function getTrans(lang: string) {
    return TRANSLATIONS[lang as keyof typeof TRANSLATIONS] ?? TRANSLATIONS.en;
}

function nowStrings(lang: string) {
    const locale = getLocale(lang);
    const now = new Date();
    return {
        date: now.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" }),
        time: now.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }),
    };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
const useExportToPDF = (t: any, filtersTable: FilterTableKey, filters: FilterDto[] = []) => {
    const [isExporting, setIsExporting] = useState(false);

    const section1Ref = useRef<HTMLDivElement>(null);
    const section2Ref = useRef<HTMLDivElement>(null);
    const section3Ref = useRef<HTMLDivElement>(null);

    const { i18n }        = useTranslation();
    const selectedFilters = useSelector((state: RootState) => state.filters[filtersTable].selectedFilters);
    const userInfo        = useUser();

    // ── captureElement ────────────────────────────────────────────────────────
    /**
     * Clones an element off-screen, waits for charts/SVGs to render,
     * captures it at high resolution and returns an ImgRecord.
     *
     * Fixes applied vs. previous version:
     *  1. setTimeout(CAPTURE_DELAY) instead of a single rAF — gives
     *     charts time to finish animating/painting before capture.
     *  2. Dimension guards — throws early if the clone or canvas ends
     *     up with 0×0, producing a clear error instead of a corrupt PNG.
     */
    const captureElement = async (el: HTMLElement): Promise<ImgRecord> => {
        const clone = el.cloneNode(true) as HTMLElement;

        // el.offsetWidth is 0 when the element is hidden, inside a collapsed
        // section, or off-screen. Fall back through several width sources so
        // the clone always has a valid layout width.
        const sourceWidth =
            el.offsetWidth > 0
                ? el.offsetWidth
                : el.getBoundingClientRect().width > 0
                    ? el.getBoundingClientRect().width
                    : document.body.clientWidth;

        // sourceHeight: used later as fallback if the clone reports 0 height.
        // Elements whose height comes purely from CSS classes (not inline styles)
        // often lose their height when cloned off-screen.
        const sourceHeight =
            el.scrollHeight > 0 ? el.scrollHeight :
            el.offsetHeight > 0 ? el.offsetHeight :
            el.getBoundingClientRect().height;

        Object.assign(clone.style, {
            position:   "fixed",
            top:        "-99999px",
            left:       "-99999px",
            maxWidth:   "none",
            maxHeight:  "none",
            overflow:   "visible",
            width:      sourceWidth + "px",
            // Set explicit height so children that use % height have a reference.
            // We'll recalculate after layout.
            height:     sourceHeight > 0 ? sourceHeight + "px" : "auto",
            visibility: "hidden",   // in DOM for layout, invisible on screen
        });

        // Force all descendant wrappers to be fully visible so charts don't clip
        const forceVisible = (node: HTMLElement) => {
            node.style.overflow = "visible";
            Array.from(node.children).forEach(c => forceVisible(c as HTMLElement));
        };
        forceVisible(clone);

        document.body.appendChild(clone);

        // Two-phase wait:
        //  1. rAF — let the browser compute layout for the clone
        //  2. setTimeout — give charts/SVGs/lazy images time to finish painting
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
        await new Promise<void>(resolve => setTimeout(resolve, CAPTURE_DELAY));

        // Reset height to auto so the clone can grow beyond sourceHeight if needed
        clone.style.height = "auto";
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

        // Remove visibility:hidden just before capture
        clone.style.visibility = "visible";

        // Height fallback chain: clone.scrollHeight → clone.offsetHeight → sourceHeight
        const w = clone.scrollWidth  > 0 ? clone.scrollWidth  : sourceWidth;
        const h =
            clone.scrollHeight > 0 ? clone.scrollHeight :
            clone.offsetHeight > 0 ? clone.offsetHeight :
            sourceHeight       > 0 ? sourceHeight       : 0;

        if (w === 0 || h === 0) {
            console.error("captureElement — diagnóstico de dimensiones:", {
                className:    el.className,
                sourceWidth,
                sourceHeight,
                cloneScrollW: clone.scrollWidth,
                cloneScrollH: clone.scrollHeight,
                cloneOffsetW: clone.offsetWidth,
                cloneOffsetH: clone.offsetHeight,
                elBCR:        el.getBoundingClientRect(),
            });
            document.body.removeChild(clone);
            throw new Error(`captureElement: alto 0 para "${el.className}" — ver consola`);
        }

        const canvas = await html2canvas(clone, {
            ...HTML2CANVAS_OPTS,
            x: 0,
            y: 0,
            width:        w,
            height:       h,
            windowWidth:  w,
            windowHeight: h,
        });

        document.body.removeChild(clone);

        if (canvas.width === 0 || canvas.height === 0) {
            throw new Error(`captureElement: canvas vacío para "${el.className}"`);
        }

        return {
            data:     canvas.toDataURL("image/png"),
            naturalW: canvas.width,
            naturalH: canvas.height,
            // Store the true CSS dimensions of the source element so calcImgDims
            // can use the real aspect ratio without depending on canvas scaling.
            cssW: w,
            cssH: h,
        };
    };

    // ── captureElementsSerial ─────────────────────────────────────────────────
    /**
     * Captures elements one by one instead of in parallel.
     * Promise.all was saturating the renderer and corrupting canvases.
     */
    const captureElementsSerial = async (elements: HTMLElement[]): Promise<ImgRecord[]> => {
        const results: ImgRecord[] = [];
        for (const el of elements) {
            results.push(await captureElement(el));
        }
        return results;
    };

    // ── calcImgDims ───────────────────────────────────────────────────────────
    /**
     * Converts an ImgRecord into PDF dimensions (mm) that:
     *  - Respect the original aspect ratio exactly
     *  - Fit within availableW (never wider than the content column)
     *  - Fit within maxH if provided (scale down proportionally, never stretch)
     *
     * naturalW/naturalH are canvas pixels captured at CANVAS_SCALE× — we must
     * divide by CANVAS_SCALE to recover the true CSS pixel dimensions before
     * computing the aspect ratio, otherwise tall sections appear squashed.
     */
    const calcImgDims = (
        img: ImgRecord,
        availableW: number,
        maxH?: number,
    ): { w: number; h: number } => {
        // Use the real CSS dimensions captured from the source element —
        // these are the true width/height before any supersampling, so the
        // aspect ratio is always exact regardless of CANVAS_SCALE.
        const aspect = img.cssH / img.cssW;

        // Start at full content width and derive height from aspect ratio
        let w = availableW;
        let h = w * aspect;

        // If a max height is set and we exceed it, scale down from height
        if (maxH && h > maxH) {
            h = maxH;
            w = h / aspect;
        }

        // Never exceed available width (safety clamp)
        if (w > availableW) {
            w = availableW;
            h = w * aspect;
        }

        return { w, h };
    };

    // ── getLogoForPdf ─────────────────────────────────────────────────────────
    const getLogoForPdf = async (userInfoArg?: any): Promise<string | null> => {
        const buildBase64 = (logo?: string | null, contentType?: string | null): string | null => {
            if (!logo) return null;
            if (logo.startsWith("data:image")) return logo;
            if (!contentType) return null;
            return `data:${contentType};base64,${logo}`;
        };

        const fromUser = buildBase64(userInfoArg?.clientLogo, userInfoArg?.clientLogoContentType);
        if (fromUser) return fromUser;

        const fromStorage = buildBase64(
            localStorage.getItem("clientLogo"),
            localStorage.getItem("clientLogoContentType"),
        );
        if (fromStorage) return fromStorage;

        const data = await getGeneralParams();
        return buildBase64(data?.logo, data?.logoContentType);
    };

    // ── addHeader ─────────────────────────────────────────────────────────────
    /**
     * Renders a logo in the PDF header respecting its real aspect ratio.
     * LOGO_W is the max width allowed; height is derived from the image
     * proportions so it never gets squashed or stretched.
     */
    const getLogoDims = (src: string): Promise<{ w: number; h: number }> =>
        new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
                const aspect = img.naturalHeight / img.naturalWidth;
                const w = LOGO_W;
                const h = w * aspect;
                resolve({ w, h });
            };
            img.onerror = () => resolve({ w: LOGO_W, h: LOGO_H }); // fallback
            img.src = src;
        });



    const getFilterConfig = (name: string) => filters.find(f => f.name === name);

    const getDisplayValue = (opt: KeyValuePair | undefined, fallback: string) => {
        if (!opt?.value) return fallback;

        if (typeof opt.value === 'object') {
            const v = opt.value as any;

            if (v.description) {
                return v.description;
            }
        }

        return String(opt.value);
    };

    const buildHeaderAdder = (
        pdf: jsPDF,
        pdfW: number,
        trans: (typeof TRANSLATIONS)["en"],
        getTitle: () => string,
        filters: typeof selectedFilters,
        logoDims: { w: number; h: number }
    ) => {
        let yPos = PDF_MARGIN;

        const addHeader = (isFirst: boolean, logo?: string | null) => {
            yPos = PDF_MARGIN;
            if (logo) {
                try {
                    pdf.addImage(logo, "PNG", PDF_MARGIN, yPos, logoDims.w, logoDims.h);
                } catch (e) {
                    console.warn("Logo inválido, usando fallback", e);
                    pdf.addImage(craiteriaLogoImage, "PNG", PDF_MARGIN, yPos, logoDims.w, logoDims.h);
                }
            } else {
                pdf.addImage(craiteriaLogoImage, "PNG", PDF_MARGIN, yPos, logoDims.w, logoDims.h);
            }
            yPos += logoDims.h + 4;

            // Title — split to multiple lines if too long to fit content width
            pdf.setFontSize(14);
            pdf.setFont("helvetica", "bold");
            const contentW = pdfW - 2 * PDF_MARGIN;
            const titleLines = pdf.splitTextToSize(getTitle(), contentW) as string[];
            const lineH = 7; // mm per line
            pdf.text(titleLines, pdfW / 2, yPos + 4, { align: "center", baseline: "middle" });
            yPos += titleLines.length * lineH + 4;

            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.3);
            pdf.line(PDF_MARGIN, yPos, pdfW - PDF_MARGIN, yPos);
            yPos += 6;

            if (isFirst && filters?.length) {
                pdf.setFontSize(10);
                pdf.setFont("helvetica", "bold");
                pdf.text(trans.filters + ":", PDF_MARGIN, yPos);
                yPos += 6;
               
                pdf.setFont("helvetica", "normal");

                filters.forEach((f) => {

                    const filterConfig = getFilterConfig(f.name);
                    if (filterConfig == null) return;
                    const options = filterConfig.values as KeyValuePair[] | undefined;;
                    let displayValue;
                    if (filterConfig.type === FilterType.DATE) {
                        if (f.dateValues?.initialDate) {
                            const description = `${f.description} ${t("dashboard:filters.Inicial")}`;
                            const value = convertIntUnixToFormateDate(f.dateValues.initialDate)
                            pdf.text(`• ${description}: ${value}`, PDF_MARGIN + 3, yPos);
                            yPos += 5;
                        }

                        if (f.dateValues?.finalDate) {
                            const description = `${f.description} ${t("dashboard:filters.Final")}`;
                            const value = convertIntUnixToFormateDate(f.dateValues.finalDate)
                            pdf.text(`• ${description}: ${value}`, PDF_MARGIN + 3, yPos);
                            yPos += 5;
                        }
                      
                 
                    } else {
                        if (f.values == null) return;
                            f.values.forEach((value) => {
                            const option = (options || []).find((opt) => String(opt.key) === String(value));
                            displayValue = getDisplayValue(option, value);
                            pdf.text(`• ${f.description}: ${displayValue}`, PDF_MARGIN + 3, yPos);
                            yPos += 5;
                        });
                    }
                
                });
                yPos += 4;
            }

            return yPos;
        };

        return { addHeader, getY: () => yPos, setY: (v: number) => { yPos = v; } };
    };

    // ── addFooter ─────────────────────────────────────────────────────────────
    const buildFooterAdder = (
        pdf: jsPDF,
        pdfW: number,
        pdfH: number,
        trans: (typeof TRANSLATIONS)["en"],
        date: string,
        time: string,
    ) => {
        const addFooter = (pageNum: number, total: number) => {
            const footerLineY = pdfH - FOOTER_H;
            const footerTextY = footerLineY - 3;

            pdf.setFillColor(248, 248, 248);
            pdf.rect(0, footerLineY, pdfW, FOOTER_H, "F");

            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.3);
            pdf.line(PDF_MARGIN, footerLineY, pdfW - PDF_MARGIN, footerLineY);

            pdf.setFontSize(7.5);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(120, 120, 120);

            pdf.text(`${trans.reportDate}: ${date}`, PDF_MARGIN, footerTextY - 4);
            pdf.text(`${trans.reportTime}: ${time}`, PDF_MARGIN, footerTextY);
            pdf.text(
                `${trans.page} ${pageNum} ${trans.of} ${total}`,
                pdfW - PDF_MARGIN, footerTextY - 2,
                { align: "right" },
            );

            pdf.setTextColor(0, 0, 0);
        };

        return { addFooter };
    };

    // ── exportToPDF ───────────────────────────────────────────────────────────
    const exportToPDF = async () => {
        setIsExporting(true);
        try {
            const el1 = section1Ref.current;
            const el2 = section2Ref.current;
            const el3 = section3Ref.current;
            if (!el1 || !el2) return;

            // Only include el3 (filters section) if it actually has visible content.
            // When filters are hidden the wrapper div exists but has height 0 —
            // passing it to captureElement would produce a corrupt/empty PNG.
            const hasContent = (el: HTMLElement) =>
                el.scrollHeight > 0 || el.offsetHeight > 0 || el.getBoundingClientRect().height > 0;

            const elements = [el1, el2, ...(el3 && hasContent(el3) ? [el3] : [])];

            // Serial capture — avoids renderer saturation / corrupt PNGs
            const images = await captureElementsSerial(elements);

            const pdf      = new jsPDF("portrait", "mm", "a4");
            const pdfW     = pdf.internal.pageSize.getWidth();
            const pdfH     = pdf.internal.pageSize.getHeight();
            const contentW = pdfW - 2 * PDF_MARGIN;

            const trans        = getTrans(i18n.language);
            const { date, time } = nowStrings(i18n.language);

            const getTitle = () => {
                const uri = el1.baseURI;
                if (uri.includes("/global/sentiment")) return t("dashboard:print.printFeelingsAll");
                if (uri.includes("/global/topics"))    return t("dashboard:print.printTopicsAll");
                return t("dashboard:print.printDashboardAll");
            };

            const logo = await getLogoForPdf(userInfo);

            // Resolve real logo dimensions before building the header so the
            // logo is never squashed regardless of its original proportions.
            const logoDims = logo
                ? await getLogoDims(logo)
                : await getLogoDims(craiteriaLogoImage as string);

            const { addHeader, getY, setY } = buildHeaderAdder(pdf, pdfW, trans, getTitle, selectedFilters, logoDims, filters);
            const { addFooter }             = buildFooterAdder(pdf, pdfW, pdfH, trans, date, time);
            let currentPage = 1;

            addHeader(true, logo);

            // Full height of a content area (below header, above footer)
            const pageContentH = () => pdfH - getY() - FOOTER_H - PDF_MARGIN;

            for (const img of images) {
                // Natural height of the image at full content width — no cap yet
                const { h: naturalH } = calcImgDims(img, contentW);

                // If the image doesn't fit in the remaining space, start a new page.
                // Exception: if we're already at the top of a fresh page (idx === 0
                // equivalent), forcing a new page would leave a blank page — skip it.
                const remaining = pageContentH();
                if (naturalH > remaining && remaining < pdfH - FOOTER_H - PDF_MARGIN * 3) {
                    pdf.addPage();
                    currentPage++;
                    addHeader(false, logo);
                }

                // Scale the image to fit within the current page's remaining space.
                // If the image is taller than a full page, cap it to the page height
                // so it always fits in one page (scaling down proportionally).
                const maxH  = Math.min(pageContentH(), pdfH - getY() - FOOTER_H - PDF_MARGIN);
                const { w: fw, h: fh } = calcImgDims(img, contentW, maxH);
                const xPos  = PDF_MARGIN + (contentW - fw) / 2;

                pdf.addImage(img.data, "PNG", xPos, getY(), fw, fh);
                setY(getY() + fh + SECTION_GAP);
            }

            for (let i = 1; i <= currentPage; i++) {
                pdf.setPage(i);
                addFooter(i, currentPage);
            }

            pdf.save(`Informe-${new Date().toISOString().split("T")[0]}.pdf`);
        } catch (err) {
            console.error("Error generating PDF:", err);
        } finally {
            setIsExporting(false);
        }
    };

    // ── buildComponentFeelingPDF ──────────────────────────────────────────────
    const buildComponentFeelingPDF = async (
        element: HTMLDivElement,
        filters: SelectedFilter[],
        title: string,
    ): Promise<jsPDF> => {
        const [img, logo] = await Promise.all([
            captureElement(element),
            getLogoForPdf(userInfo),
        ]);

        const pdf      = new jsPDF("portrait", "mm", "a4");
        const pdfW     = pdf.internal.pageSize.getWidth();
        const pdfH     = pdf.internal.pageSize.getHeight();
        const contentW = pdfW - 2 * PDF_MARGIN;

        const trans        = getTrans(i18n.language);
        const { date, time } = nowStrings(i18n.language);

        let yPos = PDF_MARGIN;

        // Resolve real logo dimensions to avoid squashing
        const logoDims = logo
            ? await getLogoDims(logo)
            : await getLogoDims(craiteriaLogoImage as string);

        // Header — logo del cliente si existe, fallback al logo de Craitera
        if (logo) {
            try {
                pdf.addImage(logo, "PNG", PDF_MARGIN, yPos, logoDims.w, logoDims.h);
            } catch (e) {
                console.warn("Logo inválido, usando fallback", e);
                pdf.addImage(craiteriaLogoImage, "PNG", PDF_MARGIN, yPos, logoDims.w, logoDims.h);
            }
        } else {
            pdf.addImage(craiteriaLogoImage, "PNG", PDF_MARGIN, yPos, logoDims.w, logoDims.h);
        }
        yPos += logoDims.h + 4;

        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        const titleLines = pdf.splitTextToSize(title, contentW) as string[];
        pdf.text(titleLines, pdfW / 2, yPos + 4, { align: "center", baseline: "middle" });
        yPos += titleLines.length * 7 + 4;

        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
        pdf.line(PDF_MARGIN, yPos, pdfW - PDF_MARGIN, yPos);
        yPos += 6;

        // Filters
        if (filters?.length) {
            pdf.setFontSize(10);
            pdf.setFont("helvetica", "bold");
            pdf.text(trans.filters + ":", PDF_MARGIN, yPos);
            yPos += 6;

            pdf.setFont("helvetica", "normal");
            filters.forEach(f => {
                if (f.values?.length) {
                    pdf.text(`• ${f.description}: ${f.values[0]}`, PDF_MARGIN + 3, yPos);
                    yPos += 5;
                }
            });
            yPos += 4;
        }

        // Image
        const maxH = pdfH - yPos - FOOTER_H - PDF_MARGIN;
        const { w, h } = calcImgDims(img, contentW, maxH);
        pdf.addImage(img.data, "PNG", PDF_MARGIN + (contentW - w) / 2, yPos, w, h);

        // Footer
        const { addFooter } = buildFooterAdder(pdf, pdfW, pdfH, trans, date, time);
        addFooter(1, 1);

        return pdf;
    };

    // ── exportToPDFComponentFeeling ───────────────────────────────────────────
    const exportToPDFComponentFeeling = async (
        element: HTMLDivElement,
        filters: SelectedFilter[],
        title: string,
        audit?: CsvAuditPayload
    ) => {
        setIsExporting(true);

        if (audit?.entity) {
            try {
                await addAuditLog(audit);
            } catch (err) {
                console.error('Error adding PDF export audit log', err);
            }
        }
        try {
            
            const pdf = await buildComponentFeelingPDF(element, filters, title);
            pdf.save(`Informe-${new Date().toISOString().split("T")[0]}.pdf`);
        } catch (err) {
            console.error("Error generating PDF:", err);
        } finally {
            setIsExporting(false);
        }
    };

    // ── exportToPDFComponentFeelingBlob ───────────────────────────────────────
    const exportToPDFComponentFeelingBlob = async (
        element: HTMLDivElement,
        filters: SelectedFilter[],
        title: string,
    ): Promise<Blob> => {
        setIsExporting(true);
        try {
            const pdf = await buildComponentFeelingPDF(element, filters, title);
            return pdf.output("blob");
        } catch (err) {
            console.error("Error generating PDF blob:", err);
            throw err;
        } finally {
            setIsExporting(false);
        }
    };

    // ── Public API ────────────────────────────────────────────────────────────
    return {
        exportToPDF,
        exportToPDFComponentFeeling,
        exportToPDFComponentFeelingBlob,
        isExporting,
        section1Ref,
        section2Ref,
        section3Ref,
    };
};

export default useExportToPDF;